import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { bindKey, isEqual, last } from '@sama/micro-dash';
import { toolNameToShapeType } from '../../../../../cross-team-shared/annotation/shared/abstract-tool';
import { ShapeLocationState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import { ShapeState2D } from '../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { VectorTaggingToolObjectless } from '../../../../../cross-team-shared/tools-names';
import { ShapeGroupOrderState } from '../../../../../team-apis/content/content-connector';
import { Point2D } from '../../../../../team-apis/otterdam/answers/shape-typings';
import { getShapeHelper } from '../../../shape-helpers/get-shape-helper';
import { ShapeTool } from './abstract-shape-tool-helper';
import {
  MultiClickShapeTool,
  MultiClickShapeToolHelper,
} from './multi-click-shape-tool-helper';

export const INCREMENTAL_SHAPE_TOOL_NAMES = ['Polygon', 'Line', 'Arrow'];

/**
 * An "incremental" shape is one that:
 * - can have any number of points, past a minimum
 * - is created by clicking each point in turn
 * - is fully rendered during creation, with its last point following the mouse
 */
export interface IncrementalShapeTool extends ShapeTool {
  getSnappedImagePoint(): Point2D;
  getBoundedImagePoint(): Point2D;
}

class IncrementalToMultiClickToolAdapter implements MultiClickShapeTool {
  name: VectorTaggingToolObjectless;
  notifyDrawFinished = bindKey(this.tool, 'notifyDrawFinished');

  constructor(private tool: IncrementalShapeTool) {
    this.name = tool.name;
  }

  buildFirstKeyLocation(initialValues: ShapeLocationState2D) {
    return { ...initialValues, points: [this.tool.getSnappedImagePoint()] };
  }

  updateLocationToFollowMouse(location: ShapeLocationState2D) {
    const lastPointIndex = location.points.length - 1;
    // the first point of the shape should always be within the image bounds
    location.points[lastPointIndex] = lastPointIndex
      ? this.tool.getBoundedImagePoint()
      : this.tool.getSnappedImagePoint();
  }

  handleInProgressMouseDown(
    helper: MultiClickShapeToolHelper,
    event: MouseEvent,
  ) {
    const points = helper.getPointsStore().state();
    const clickedPoint = last(points);

    if (isEqual(clickedPoint, points[points.length - 2])) {
      if (
        points.length <=
        getShapeHelper(toolNameToShapeType(this.name)).getMinPoints()
      ) {
        return;
      }

      void helper.stopListening(event);
    } else {
      helper.addPoint(clickedPoint);
      helper.trackAddInProgressSubpointEvent(points.length - 1);
    }
  }

  prepareKeyLocationForCommit(location: ShapeLocationState2D) {
    // The last location point is the in progress point under the mouse so we
    // need to remove it before committing the new shape
    location.points.splice(-1, 1);
  }

  buildShape(initialValues: ShapeState2D): ShapeState2D {
    return this.tool.buildShape(initialValues);
  }
}

export class IncrementalShapeToolHelper extends MultiClickShapeToolHelper {
  constructor(
    tool: IncrementalShapeTool,
    overlayStore: Store<ShapeGroupOrderState>,
    injector: Injector,
  ) {
    super(new IncrementalToMultiClickToolAdapter(tool), overlayStore, injector);
  }
}
